import { isNil } from 'lodash';
import { useCallback, useRef, useState } from 'react';
import { Button } from '@shared/components/Button';
import { Modal } from '@shared/components/Modal';
import { View } from '@shared/components/View';
import { Carousel } from '@shared/components/carousel';
import { API } from '@shared/types/api';
import { FileUpload } from '@shared/types/fieldValues';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { isTouchDevice } from '@shared/util/isTouchDevice';
import { downloadFullSizeImageUri } from '@shared/util/media/image';
import { MBZoomableImage } from '../image/zoomable';
import { ImagePreviewProps } from './types';
import styles from './ImagePreviewModal.module.css';

/*
    Image preview lightbox on web.
*/
export function ImagePreviewModal({ onClose, showIndex, images, visible }: ImagePreviewProps) {
  const previewModalRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(showIndex);
  const [isZoomed, onZoomStart, onZoomEnd] = useBoolean(false);
  const activeImage = isNil(activeIndex) ? undefined : images?.[activeIndex];
  const filename = activeImage?.name;

  const handleDownload = () => {
    if (isNil(activeImage)) {
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = downloadFullSizeImageUri(activeImage.url);
    anchor.target = '_blank';
    anchor.download = filename || '';
    anchor.click();
  };

  const onIndexChange = (i: number) => {
    setActiveIndex(i);
  };

  const shouldHaveHorizontalMargins = images && images.length > 1 && !isTouchDevice;

  const renderItem = useCallback(
    (image: FileUpload | API.ChatAttachmentFragment) => (
      <MBZoomableImage
        image={image}
        shouldHaveHorizontalMargins={shouldHaveHorizontalMargins}
        onZoomStart={onZoomStart}
        onZoomEnd={onZoomEnd}
      />
    ),
    [shouldHaveHorizontalMargins, onZoomStart, onZoomEnd],
  );

  return (
    <Modal
      fullScreen
      mode="modal"
      isOpen={visible}
      onOpenChange={onClose}
      data-theme="dark"
      className={styles.modal}
      testID="image-preview-modal"
    >
      <View stopPropagation fill ref={previewModalRef}>
        <Modal.TitleBar
          showBorder={false}
          title={filename}
          startContent={<Modal.CloseButton onClick={onClose} />}
          endContent={
            <Button
              variant="ghost"
              iconOnly
              icon="arrow-move-down"
              iconGridSize={3.75}
              label={i18n('common.download')}
              onClick={handleDownload}
            />
          }
        />
        {images && (
          <Carousel<FileUpload | API.ChatAttachmentFragment>
            data={images}
            initialActiveIndex={showIndex}
            onChangeActiveIndex={onIndexChange}
            hotkeyTarget={previewModalRef}
            showNavButtons={!isTouchDevice || isZoomed}
            renderItem={renderItem}
          />
        )}
      </View>
    </Modal>
  );
}
